import { availableLocales, DEFAULT_LOCALE } from './index';
import { PREFIX_ALTERNATION } from './types';
import type { Locale } from './types';
import { blogLocalesFor } from '../data/articles';

export interface HreflangLink {
  rel: 'alternate';
  hreflang: string;
  href: string;
}

function localizedPath(locale: Locale, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return locale === DEFAULT_LOCALE ? clean : `/${locale}${clean === '/' ? '' : clean}`;
}

// Agent-facing landing pages are EN-only — no localized variants exist, so they
// must not advertise /ru,/es,/pt,/tr hreflang alternates. Mirrors backend
// util/hreflang.ts EN_ONLY_PATHS + seo.ts.
const EN_ONLY_PATHS = new Set(['/ai-agents', '/mcp-server', '/agents-earn', '/data-price-index', '/glossary']);

export function buildHreflangLinks(currentPath: string, baseUrl: string): HreflangLink[] {
  const origin = baseUrl.replace(/\/+$/, '');
  const path = currentPath.replace(new RegExp(`^/(?:${PREFIX_ALTERNATION})(?=/|$)`), '') || '/';

  if (EN_ONLY_PATHS.has(path.replace(/\/$/, '')) || /^\/regions\/esim-[a-z-]+\/?$/.test(path)) {
    const href = `${origin}${path.replace(/\/$/, '')}`;
    return [
      { rel: 'alternate', hreflang: 'en', href },
      { rel: 'alternate', hreflang: 'x-default', href },
    ];
  }

  // Blog posts may be only partially localized. Advertise hreflang ONLY for
  // the locales that actually have a translation (from the EN entry's
  // `locales` field) — else Google crawls e.g. /es/blog/<slug> that serves EN
  // fallback and flags it duplicate/redirect. Mirrors backend util/hreflang.ts
  // + sitemap-blog.xml (which already emits only present locales).
  const blogMatch = path.match(/^\/blog\/([a-z0-9-]+)$/);
  if (blogMatch) {
    const present = blogLocalesFor(blogMatch[1]!);
    const out: HreflangLink[] = present.map((locale) => ({
      rel: 'alternate' as const,
      hreflang: locale,
      href: `${origin}${localizedPath(locale, path)}`,
    }));
    out.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${origin}${localizedPath(DEFAULT_LOCALE, path)}`,
    });
    return out;
  }

  const links: HreflangLink[] = availableLocales.map((locale) => ({
    rel: 'alternate' as const,
    hreflang: locale,
    href: `${origin}${localizedPath(locale, path)}`,
  }));

  links.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${origin}${localizedPath(DEFAULT_LOCALE, path)}`,
  });

  return links;
}

/**
 * Render hreflang links into the document <head> for the current path.
 * Removes any existing alternate links first so route changes don't leak.
 */
export function applyHreflangLinks(currentPath: string, baseUrl: string = 'https://roamzy.io'): void {
  if (typeof document === 'undefined') return;
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  const links = buildHreflangLinks(currentPath, baseUrl);
  for (const l of links) {
    const link = document.createElement('link');
    link.setAttribute('rel', l.rel);
    link.setAttribute('hreflang', l.hreflang);
    link.setAttribute('href', l.href);
    document.head.appendChild(link);
  }
}
